<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useFullscreen } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/store/modules/app';
import { useThemeStore } from '@/store/modules/theme';
import { useRouteStore } from '@/store/modules/route';
import { useAuthStore } from '@/store/modules/auth';
import { useRouterPush } from '@/hooks/common/router';
import { useFootStore } from '@/store/modules/layoutFoot';
import { getPageInfo } from '@/store/modules/route/shared';
import HorizontalMenu from '../global-menu/base-menu.vue';
import GlobalLogo from '../global-logo/index.vue';
import GlobalBreadcrumb from '../global-breadcrumb/index.vue';
import { useMixMenuContext } from '../../context';
import UserAvatar from './components/user-avatar.vue';
import UserSpace from './components/user-space.vue';

const { removeAll } = useFootStore();

const { systemInfo } = useAuthStore();

defineOptions({
  name: 'GlobalHeader'
});

interface Props {
  /** Whether to show the logo */
  showLogo?: App.Global.HeaderProps['showLogo'];
  /** Whether to show the menu toggler */
  showMenuToggler?: App.Global.HeaderProps['showMenuToggler'];
  /** Whether to show the menu */
  showMenu?: App.Global.HeaderProps['showMenu'];

  showOnlyLink?: App.Global.HeaderProps['showOnlyLink'];
}

const props = defineProps<Props>();

// console.log(props.showOnlyLink);

const appStore = useAppStore();
const themeStore = useThemeStore();
const routeStore = useRouteStore();
const { isFullscreen, toggle } = useFullscreen();
const { menus } = useMixMenuContext();

const headerMenus = computed(() => {
  if (themeStore.layout.mode === 'horizontal') {
    return routeStore.menus;
  }

  if (themeStore.layout.mode === 'horizontal-mix') {
    return menus.value;
  }

  return [];
});
const { routerBack } = useRouterPush();
const route = useRouter();

const homeFlag = computed(() => route?.currentRoute.value.path === '/home');

const titleSupple = computed(() =>
  route?.currentRoute.value.query.titleSupple ? `  |  ${route?.currentRoute.value.query.titleSupple}` : ''
);

function headerBack() {
  routerBack();
  removeAll();
}
</script>

<template>
  <div class="h-56px bg-primary">
    <div class="h-full w-full flex-y-center justify-between shadow-header">
      <NSpace align="center">
        <MenuToggler v-if="!props.showOnlyLink" :collapsed="appStore.siderCollapse"
          @click="appStore.toggleSiderCollapse" />
        <div class="text-lg text-white" :class="{
          'pl-14px': props.showOnlyLink,
          'font-light opacity-90': homeFlag
        }">
          {{ !homeFlag ? $t("system.title") : $t('page.home.welcome') }}
          <!-- {{ !homeFlag ? systemInfo.systemName : $t('page.home.welcome') }} -->
        </div>
      </NSpace>
      <div class="h-full flex-y-center">
        <NButton quaternary type="primary">
          <SvgIcon local-icon="help-light" class="text-2xl" />
        </NButton>
        <LangSwitch :lang="appStore.locale" :lang-options="appStore.localeOptions" icon-url="lang-light"
          @change-lang="appStore.changeLocale" />
        <FullScreen v-if="!appStore.isMobile" :full="isFullscreen" @click="toggle" />
        <UserSpace />
        <UserAvatar />
      </div>
    </div>
  </div>
  <!-- 项目功能模块导航栏 -->
  <div v-if="!props.showOnlyLink && !homeFlag" class="h-60px bg-white px-24px">
    <div class="h-full flex-y-center">
      <div v-if="appStore.headerFlag" class="my-8px text-2xl text-deepBlack">
        {{ $t(routeStore.pageInfo.pageTitle) }}
      </div>
    </div>
    <div v-show="themeStore.header.close.visible" class="absolute right-24px top-70px rounded hover:bg-slate-100"
      @click="headerBack">
      <icon-local-close class="m-1 text-24px" />
    </div>
  </div>
  <!-- 首页导航栏内容 -->
  <div v-if="!props.showOnlyLink && homeFlag" class="h-146px bg-primary px-58px">
    <div class="text-3xl text-white">
      {{ $t("system.title") }}
      <!-- {{ systemInfo.systemName }} -->
    </div>
  </div>
  <!-- 个人中心导航栏内容 -->
  <div v-if="props.showOnlyLink" class="mx-auto h-48px w-80% flex-y-center">
    <div class="flex-y-center cursor-pointer text-2xl color-deepBlack" @click="routerBack">
      <icon-local-line-md-chevron-triple-left class="mr-12px" />
      {{ $t(routeStore.pageInfo.pageTitle) }}
    </div>
  </div>
</template>

<style scoped></style>
